import i18next from 'i18next';
import { DateTime } from 'luxon';

import { queryEquals, queryIn } from '@work4all/utils';
import { RelativeDateFilter } from '@work4all/utils/lib/date-utils/RelativeDateFilter.enum';

export const searchToQuery = (value: number | string) => {
  if (typeof value === 'string') {
    return queryEquals(`%${value.trim()}%`);
  } else {
    return queryEquals(`${value}`);
  }
};

export const booleanToQuery = (
  value: { id: number | string; name: string }[]
) => {
  if (value.length === 1) {
    return queryEquals(`${value[0].id ? true : false}`);
  }
};

export const relativeDateToDate = (
  startDate: Date | RelativeDateFilter,
  endDate: Date
) => {
  if ((startDate as RelativeDateFilter) in RelativeDateFilter) {
    switch (startDate as RelativeDateFilter) {
      case RelativeDateFilter.currentMonth:
        return {
          startDate: DateTime.now().startOf('month'),
          endDate: DateTime.now().endOf('month'),
        };
      case RelativeDateFilter.currentWeek:
        return {
          startDate: DateTime.now().startOf('week'),
          endDate: DateTime.now().endOf('week'),
        };
      case RelativeDateFilter.currentYear:
        return {
          startDate: DateTime.now().startOf('year'),
          endDate: DateTime.now().endOf('year'),
        };
      case RelativeDateFilter.today:
        return {
          startDate: DateTime.now().startOf('day'),
          endDate: DateTime.now().endOf('day'),
        };
      case RelativeDateFilter.todayAndPast:
        return {
          endDate: DateTime.now().endOf('day'),
        };
    }
  } else {
    return {
      startDate: !startDate ? null : DateTime.fromJSDate(startDate as Date),
      endDate: !endDate ? null : DateTime.fromJSDate(endDate as Date),
    };
  }
};

export const dateToQuery = (
  startDate: Date | RelativeDateFilter,
  endDate: Date
) => {
  const date = relativeDateToDate(startDate, endDate);

  return {
    $gte: date?.startDate?.toString(),
    $lt: date?.endDate?.toString(),
  };
};

export const numbersToQuery = (from: number | null, to: number | null) => {
  const result = {};
  if (from !== null) {
    result['$gte'] = from;
  }
  if (to !== null) {
    result['$lt'] = to;
  }
  return result;
};

export const pickerToQuery = (values: []) => {
  return queryIn(values.map(({ id }) => id));
};

export const numbersToString = (from: number, to: number): string => {
  if (from != null && to != null) {
    return from + ' - ' + to;
  }
  if (from != null) {
    return i18next.t('COMMON.TIME_FROM') + ' ' + from;
  }
  if (to != null) {
    return i18next.t('COMMON.TIME_UNTIL') + ' ' + to;
  }
};

export const dateToString = (
  startDate: Date | RelativeDateFilter,
  endDate: Date,
  withPrefix = true
): string => {
  if (!startDate && !endDate) {
    return null;
  }
  if ((startDate as RelativeDateFilter) in RelativeDateFilter) {
    const relativeFilterStrings: Record<RelativeDateFilter, string> = {
      [RelativeDateFilter.currentMonth]: i18next.t('COMMON.THIS_MONTH'),
      [RelativeDateFilter.currentWeek]: i18next.t('COMMON.THIS_WEEK'),
      [RelativeDateFilter.currentYear]: i18next.t('COMMON.THIS_YEAR'),
      [RelativeDateFilter.today]: i18next.t('COMMON.TODAY'),
      [RelativeDateFilter.todayAndPast]: i18next.t('COMMON.TODAY_AND_PAST'),
    };
    return relativeFilterStrings[startDate as RelativeDateFilter];
  }

  return timeSpanFormat(startDate as Date, endDate, withPrefix);
};

export const timeSpanFormat = (
  startDate: Date,
  endDate: Date,
  withPrefix = true
) => {
  if ((startDate === null || startDate === undefined) && endDate !== null) {
    return (
      (withPrefix ? i18next.t('COMMON.TIME_UNTIL') + ' ' : '') +
      DateTime.fromJSDate(endDate).toFormat('d.M.yyyy')
    );
  }

  if (startDate !== null && (endDate === null || endDate === undefined)) {
    return (
      (withPrefix ? i18next.t('COMMON.TIME_FROM') + ' ' : '') +
      DateTime.fromJSDate(startDate).toFormat('d.M.yyyy')
    );
  }

  let startFormatDay = 'dd.';
  let startFormatMonth = 'M.';
  let startFormatYear = 'yyyy';

  if (
    DateTime.fromJSDate(startDate).year === DateTime.fromJSDate(endDate).year
  ) {
    startFormatYear = '';
    if (
      DateTime.fromJSDate(startDate).month ===
      DateTime.fromJSDate(endDate).month
    ) {
      startFormatMonth = '';
      startFormatDay = 'dd';
    }
  }

  if (
    startDate &&
    endDate &&
    DateTime.fromJSDate(startDate).hasSame(DateTime.fromJSDate(endDate), 'day')
  ) {
    return DateTime.fromJSDate(endDate).toFormat('dd.M.yyyy');
  }

  return (
    DateTime.fromJSDate(startDate).toFormat(
      startFormatDay + startFormatMonth + startFormatYear
    ) +
    ' - ' +
    DateTime.fromJSDate(endDate).toFormat('dd.M.yyyy')
  );
};
